"use client";
import { useLenis } from "lenis/react";
import { useEffect, useState } from "react";

type UseScrollDirectionOptions = {
  /**
   * Base scroll threshold before considering hiding header
   * @default 200
   */
  baseThreshold?: number;
  /**
   * Minimum scroll delta to trigger direction change
   * @default 10
   */
  minScrollDelta?: number;
  /**
   * Scroll position threshold for "scrolled" state styling
   * @default 100
   */
  scrolledThreshold?: number;
  /**
   * Maximum delay for hiding header (in ms)
   * @default 800
   */
  maxHideDelay?: number;
  /**
   * Mouse hover area height from top (in px) to reveal header
   * @default 100
   */
  hoverRevealHeight?: number;
};

type ScrollDirectionState = {
  /** Current scroll direction */
  scrollDirection: "up" | "down";
  /** Whether page is scrolled past threshold (for styling) */
  isScrolled: boolean;
  /** Whether header should be visible */
  isHeaderVisible: boolean;
  /** Current scroll position */
  scrollY: number;
  /** Whether component is mounted (for SSR safety) */
  isMounted: boolean;
};

/**
 * Custom hook for progressive scroll direction detection with header visibility logic
 *
 * Features:
 * - Progressive hiding: waits longer to hide header when deeper in page
 * - Dynamic thresholds: requires more scrolling to hide when deep
 * - Time-based delays: adds delay before hiding based on scroll depth
 * - Mouse hover reveal: shows header when hovering near top
 * - Smart overrides: respects menu/modal states
 */
export function useScrollDirection(
  options: UseScrollDirectionOptions = {},
  overrides: {
    /** Prevent hiding when mobile menu is open */
    isMenuOpen?: boolean;
    /** Prevent hiding when search is open */
    isSearchOpen?: boolean;
  } = {}
): ScrollDirectionState {
  const {
    baseThreshold = 200,
    minScrollDelta = 10,
    scrolledThreshold = 100,
    maxHideDelay = 800,
    hoverRevealHeight = 100,
  } = options;

  const { isMenuOpen = false, isSearchOpen = false } = overrides;

  // State
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  // Mount detection for SSR safety
  useEffect(() => {
    setIsMounted(true);
    // Initialize scroll position after mount to prevent hydration mismatch
    if (typeof window !== "undefined") {
      setLastScrollY(window.scrollY || 0);
      setIsScrolled((window.scrollY || 0) > scrolledThreshold);
    }
  }, [scrolledThreshold]);

  // Enhanced scroll detection with progressive behavior
  useLenis(({ scroll }) => {
    if (!isMounted) {
      return;
    }

    const currentScrollY = scroll;

    // Basic scroll state for styling
    setIsScrolled(currentScrollY > scrolledThreshold);

    // Scroll direction detection
    const direction = currentScrollY > lastScrollY ? "down" : "up";

    // Only update if direction actually changed
    if (direction !== scrollDirection) {
      setScrollDirection(direction);
    }

    // Progressive header visibility logic
    const scrollDelta = Math.abs(currentScrollY - lastScrollY);

    // Calculate dynamic threshold based on scroll depth
    const scrollDepthFactor = Math.min(currentScrollY / 1000, 2); // Cap at 2x
    const dynamicHideThreshold = baseThreshold + scrollDepthFactor * 150;

    // Minimum scroll delta required to hide (increases with depth)
    const minRequiredDelta = 15 + scrollDepthFactor * 25; // 15px to 65px

    // Only process if we've scrolled enough to matter
    if (scrollDelta > minScrollDelta) {
      if (currentScrollY < baseThreshold) {
        // Always show header near top of page
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          setHideTimeout(null);
        }
        setIsHeaderVisible(true);
      } else if (
        direction === "down" &&
        currentScrollY > dynamicHideThreshold
      ) {
        // Hide header when scrolling down with progressive threshold and delay
        if (!(isMenuOpen || isSearchOpen) && scrollDelta > minRequiredDelta) {
          // Clear any existing timeout
          if (hideTimeout) {
            clearTimeout(hideTimeout);
          }

          // Calculate delay based on scroll depth (0-maxHideDelay ms)
          const hideDelay = Math.min(
            scrollDepthFactor * (maxHideDelay / 2),
            maxHideDelay
          );

          // Set timeout to hide header after delay
          const timeout = setTimeout(() => {
            setIsHeaderVisible(false);
          }, hideDelay);

          setHideTimeout(timeout);
        }
      } else if (direction === "up") {
        // Show header when scrolling up (cancel any hide timeout)
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          setHideTimeout(null);
        }
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    }
  });

  // Show header on hover near top of screen
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      // Show header if mouse is in top area and header is hidden
      if (
        e.clientY <= hoverRevealHeight &&
        !isHeaderVisible &&
        !isMenuOpen &&
        !isSearchOpen
      ) {
        // Cancel any pending hide timeout
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          setHideTimeout(null);
        }
        setIsHeaderVisible(true);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [
    isMounted,
    isHeaderVisible,
    isMenuOpen,
    isSearchOpen,
    hideTimeout,
    hoverRevealHeight,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  // Force header visible when menus are open
  useEffect(() => {
    if (isMenuOpen || isSearchOpen) {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        setHideTimeout(null);
      }
      setIsHeaderVisible(true);
    }
  }, [isMenuOpen, isSearchOpen, hideTimeout]);

  return {
    scrollDirection,
    isScrolled,
    isHeaderVisible,
    scrollY: lastScrollY,
    isMounted,
  };
}
